import { Component, Prop } from 'vue-property-decorator'
import Vue from '@src/vue'
import { CreateElement } from 'vue'

import { Icon } from '@src/elements/icon/icon'
import { ExerciseResponse } from '@src/data/exercise-response'
import { H5, Subtitle2 } from '@src/elements/typography/typography'
import { Button } from '@src/elements/button/button'
import { PrintTicketQR } from '@src/routes/support/components/exercises/print-ticket-qr'
import { TicketModal } from '@src/routes/support/components/exercises/ticket-modal'

import { AffectedProduct, ExerciseSupportTicket, StateRecording, StateTicket } from '@src/data/support-ticket'
import { convertDateTo } from '@src/util/date-format'

import './ticket.sass'
import { API } from '@src/lib/api_path'
import EventBus from '@src/lib/event_bus'
import ApiClient from '@src/lib/api_client'

@Component({
  computed: {
    availableExercises () {
      return this.$store.state.availableExercises
    }
  }
})
export class Ticket extends Vue {

  @Prop({ required: true })
  private supportTicket: ExerciseSupportTicket

  @Prop({ required: false })
  private position: number

  private availableExercises: ExerciseResponse[]
  private showModal: boolean = false

  private printMediaQueryList: MediaQueryList

  created () {
    window.addEventListener('afterprint', this.resetPrintQR)
    this.printMediaQueryList = window.matchMedia('print')
    this.printMediaQueryList.addListener(this.onPrintMediaChange)
  }

  beforeDestroy () {
    window.removeEventListener('afterprint', this.resetPrintQR)
    this.printMediaQueryList.removeListener(this.onPrintMediaChange)
  }

  onPrintMediaChange (mql: MediaQueryListEvent) {
    if (!mql.matches) {
      this.resetPrintQR()
    }
  }

  showHideTicketModal () {
    this.showModal = !this.showModal
  }

  printQR (ticketId) {
    let ticket = this.$refs[`printTicketQR${ticketId}`] as HTMLElement
    if (ticket) {
      // add a class to the selected workout card, to be able to style and show the content in the print view
      ticket.classList.add('support-ticket-to-print-wrapper')
      window.print()
    }
  }

  resetPrintQR () {
    let ticket = this.$refs[`printTicketQR${this.supportTicket.id}`] as HTMLElement
    if (ticket) {
      // remove the class of the selected workout card to be printed
      ticket.classList.remove('support-ticket-to-print-wrapper')
    }
  }

  render (h: CreateElement) {
    const exercise = this.loadExercise(this.supportTicket.details.exerciseId!)
    return <div class='exercise-ticket' ref={`printTicketQR${this.supportTicket.id}`}>
      <div class='title'>
        {/*TODO*/}
        {this.supportTicket.problemType === AffectedProduct.STATION_LEGACY ? <Icon name='brand/3d' /> : <Icon name='brand/2d' />}
        <H5>{exercise && exercise.getName()}</H5>
      </div>
      <div class='columns'>
        <div class='column'>
          <div class='row'>
            <Subtitle2 ref='exercise-number'>{this.$t('SUPPORT_PAGE.SUPPORT_TICKET.EXERCISE_NUMBER')}<span class='blue'>{exercise && exercise.number}</span></Subtitle2>
            <Subtitle2 ref='ticket-number'>{this.$t('SUPPORT_PAGE.SUPPORT_TICKET.TICKET_NUMBER')}<span class='blue'>{this.supportTicket.id}</span></Subtitle2>
            <Subtitle2 ref='ticket-date'>{this.$t('SUPPORT_PAGE.SUPPORT_TICKET.TICKET_DATE')}<span class='blue'>{convertDateTo(this.supportTicket.date, 'MMM Do, YYYY')}</span></Subtitle2>
          </div>

          {this.supportTicket.state === StateTicket.DRAFT ?
            <div class='row'>
              <Subtitle2 class='download-qr-wrapper'>
                <Button class='download-qr' icon='brand/qr-code' type='remove' label={this.$t('PAGE.MEMBERS.QR_CODE.DOWNLOAD')}
                        click={() => this.printQR(this.supportTicket.id)} />
              </Subtitle2>
              {this.supportTicket.details.stateRecording === StateRecording.NOT_STARTED ?
                <Subtitle2 class='recording-status'><Icon name='util/not-recorded' />{this.$t('SUPPORT_PAGE.SUPPORT_TICKET.NOT_RECORDED')}</Subtitle2> :
                this.supportTicket.details.stateRecording === StateRecording.STARTED ?
                  <Subtitle2 class='recording-status recording'><Icon name='util/recording' />{this.$t('SUPPORT_PAGE.SUPPORT_TICKET.RECORDING')}</Subtitle2> :
                  this.supportTicket.details.stateRecording === StateRecording.FINISHED_AND_UPLOADING ?
                    <Subtitle2 class='recording-status'><Icon name='util/circle-arrows' />{this.$t('SUPPORT_PAGE.SUPPORT_TICKET.UPLOADING')}</Subtitle2> :
                    this.supportTicket.details.stateRecording === StateRecording.FINISHED_AND_UPLOADED ?
                      <Subtitle2 class='recording-status'><Icon name='util/recorded' />{this.$t('SUPPORT_PAGE.SUPPORT_TICKET.RECORDED')}</Subtitle2> :
                      null}
              {this.position ?
                <Subtitle2 class='queue'>
                  <div class='queue-icon'>
                    <div class={`queue-position ${this.position > 99 && 'long'}`}>{this.position}</div>
                    <Icon name='util/queue' />
                  </div>
                  {this.position === 1 ? this.$t('SUPPORT_PAGE.SUPPORT_TICKET.NEXT') : ''}</Subtitle2>
                : <Subtitle2 />}
            </div>
            : null}

          {this.supportTicket.state === StateTicket.OPEN ?
            <div class='row'>
              <Subtitle2 class='recording-status'><Icon name='util/recorded' />{this.$t('SUPPORT_PAGE.SUPPORT_TICKET.RECORDED')}</Subtitle2>
            </div>
            : null}
          <PrintTicketQR supportTicket={this.supportTicket} />
        </div>
        <div class='row'>
        <div class='column'>
          <div class='view-details'>
            <Button type='outline' label={this.$t('SUPPORT_PAGE.SUPPORT_TICKET.VIEW_TICKET')} click={this.showHideTicketModal} />
            { this.supportTicket.state === StateTicket.DRAFT ?
              <Button label={this.$t('SUPPORT_PAGE.SUPPORT_TICKET.MODAL.SUBMIT_TICKET')}
                      click={this.submitTicket}
                      disabled={!this.supportTicket.video}
                      ref='submitBtn'/>
              : null }
          </div>
        </div>
        </div>
      </div>
      {this.showModal ? <TicketModal action='edit' supportTicket={this.supportTicket} closeCallback={this.showHideTicketModal}/> : null}
    </div>
  }

  private submitTicket () {
    new ApiClient().post(`${API.TRAINERS_API.SUPPORT_TICKETS}/${this.supportTicket.id}/submit`,
      {})
      .then(() => {
        EventBus.trigger('reloadSupportTickets')
        this.$store.dispatch('showToast', { message: this.$t('SUPPORT_PAGE.SUPPORT_TICKET.UPDATED'), fadeOut: true })
      })
      .catch(error => console.log('cannot submit support ticket', error))
  }

  private loadExercise (exerciseId: number): ExerciseResponse {
    return this.availableExercises.filter(exercise => exercise.id === exerciseId)[0] || null
  }
}
